/*
 * <window-dimensions> — drives variant selection from width/height inputs.
 *
 * Behaviour:
 * - Reads a JSON map of "WxH" → option value label embedded in the element,
 *   then snaps width and height to a size that actually exists in the
 *   variant data, configurable via data-snap-mode (up | nearest | down).
 * - Optional secondary option (data-secondary-option-name) is also driven
 *   from the WIDTH input, with its own snap mode. Used for BC parity on
 *   Sound Lock door seal kits where the bottom seal width derives from
 *   the door width.
 *
 * Sequencing (important):
 * Both options are set silently (no change event), then ONE change event
 * is dispatched at the end on a currently-LIVE checked primary radio.
 * Why:
 *   1. Setting silently avoids triggering a section re-render in the
 *      middle of our work — that would invalidate the radios we still
 *      need to interact with.
 *   2. We dispatch from a fresh live radio so the event reaches the
 *      currently-mounted <variant-selects> and <product-info>. Stale
 *      refs (after a previous re-render) bubble through detached DOM
 *      and the live <product-info>'s `this.contains(event.target)`
 *      check returns false, silently dropping the variant update.
 *   3. We re-look up DOM refs at the start of every _apply so a section
 *      replacement between user inputs doesn't poison subsequent runs.
 *
 * Verbose instrumentation: filter the console by [wd]. Set
 * window.WD_DEBUG = false to silence.
 */

(function () {
  // Raw, customer-typed width/height per section id. Persisted at module
  // scope so it survives the product-info section re-render that fires on
  // every variant change — letting us restore exactly what the customer
  // typed (e.g. 35 x 83) rather than repopulating from the matched variant's
  // tier max (36 x 84).
  const WD_VALUES = {};

  const dbg = (...args) => {
    if (window.WD_DEBUG === false) return;
    console.log('[wd]', ...args);
  };
  const dbgGroup = (label, fn) => {
    if (window.WD_DEBUG === false) { fn(); return; }
    console.groupCollapsed('[wd] ' + label);
    try { fn(); } finally { console.groupEnd(); }
  };

  if (customElements.get('window-dimensions')) return;

  customElements.define(
    'window-dimensions',
    class WindowDimensions extends HTMLElement {
      constructor() {
        super();
        this._debounceMs = 350;
        this._debounceTimer = null;
        this._instanceId = Math.random().toString(36).slice(2, 8);
      }

      connectedCallback() {
        if (this._connected) return;
        this._connected = true;

        this.optionName = this.dataset.optionName || 'Dimensions';
        this.widthInput = this.querySelector('[data-window-width]');
        this.heightInput = this.querySelector('[data-window-height]');
        this.warningEl = this.querySelector('[data-window-warning]');

        const modeRaw = (this.dataset.snapMode || 'up').toLowerCase();
        this.snapMode = ['up', 'nearest', 'down'].includes(modeRaw) ? modeRaw : 'up';

        this.secondaryOptionName = (this.dataset.secondaryOptionName || '').trim();
        const secModeRaw = (this.dataset.secondarySnapMode || 'nearest').toLowerCase();
        this.secondarySnapMode = ['up', 'nearest', 'down'].includes(secModeRaw)
          ? secModeRaw
          : 'nearest';

        const mapScript = this.querySelector('script[data-window-dimensions-map]');
        try {
          this.map = mapScript ? JSON.parse(mapScript.textContent) : {};
        } catch (e) {
          dbg(`#${this._instanceId} JSON parse FAILED on map script`, e);
          this.map = {};
        }

        this._refreshRefs(/* invalidateAll */ true);

        this._stableRoot = this.closest('[data-section]') || document.body;
        if (!this._observer) {
          this._observer = new MutationObserver(() => this._scheduleReattach());
          this._observer.observe(this._stableRoot, {
            childList: true,
            subtree: true,
          });
        }

        this._sectionKey = this.dataset.section || 'default';

        const onInput = () => {
          WD_VALUES[this._sectionKey] = {
            w: this.widthInput ? this.widthInput.value : '',
            h: this.heightInput ? this.heightInput.value : '',
          };
          clearTimeout(this._debounceTimer);
          this._debounceTimer = setTimeout(() => this._apply(), this._debounceMs);
        };
        if (this.widthInput) this.widthInput.addEventListener('input', onInput);
        if (this.heightInput) this.heightInput.addEventListener('input', onInput);

        dbgGroup(`#${this._instanceId} connected`, () => {
          dbg('optionName       =', this.optionName);
          dbg('snapMode         =', this.snapMode);
          dbg('secondaryName    =', this.secondaryOptionName || '(none)');
          dbg('secondarySnap    =', this.secondarySnapMode);
          dbg('map keys         =', Object.keys(this.map));
          dbg('variantSelects   =', this.variantSelects ? this.variantSelects.id || '(no id)' : 'NOT FOUND');
          dbg('dimensionsField  =', this.dimensionsField ? 'found' : 'NOT FOUND');
          dbg('dimensionMode    =', this._dimensionMode);
          dbg('extracted sizes  =', this._extractSizes());
        });
        this._logFormState('connected — initial form state');

        // Restore the customer's typed values after a section re-render, then
        // recompute state. The matching variant is already selected at this
        // point, so _apply() is a no-op on the option (no change event, no
        // re-render loop) — it just re-derives the ready/out-of-range state.
        // On a genuine first load there's nothing stored, so we stay in the
        // server-rendered 'incomplete' state with both boxes empty.
        const saved = WD_VALUES[this._sectionKey];
        if (saved && (saved.w !== '' || saved.h !== '')) {
          if (this.widthInput) this.widthInput.value = saved.w;
          if (this.heightInput) this.heightInput.value = saved.h;
          this._apply();
        } else {
          this._setState('incomplete');
        }
      }

      disconnectedCallback() {
        clearTimeout(this._debounceTimer);
        if (this._observer) {
          this._observer.disconnect();
          this._observer = null;
        }
        dbg(`#${this._instanceId} disconnected`);
      }

      // -----------------------------------------------------------------
      // Reference refresh — called at the start of every _apply, and from
      // the MutationObserver's rAF callback. Always returns a live ref to
      // <variant-selects>, and clears all child caches if it changed.
      // -----------------------------------------------------------------
      _refreshRefs(invalidateAll) {
        const liveVS = this._findVariantSelects();
        const replaced = liveVS !== this.variantSelects;
        if (invalidateAll || replaced) {
          if (replaced) dbg(`#${this._instanceId} variant-selects replaced — invalidating caches`);
          this.variantSelects = liveVS;
          this._secondaryField = null;
          this._secondaryCandidates = null;
          this._dimensionMode = undefined;
          this._dimensionSelect = undefined;
          this.dimensionsField = undefined;
        }
        // Always re-find the primary field so dimensionsField points at a live
        // wrapper even when variant-selects was kept but its innerHTML rewritten.
        this._hideDimensionsField();
        return replaced;
      }

      _logFormState(label) {
        if (window.WD_DEBUG === false) return;
        const out = { label };
        if (!this.variantSelects) {
          out.note = 'no variantSelects ref';
        } else {
          const names = [this.optionName, this.secondaryOptionName].filter(Boolean);
          for (const name of names) {
            const sel = this.variantSelects.querySelector(
              `select[name="options[${name}]" i]`
            );
            if (sel) {
              out[name] = `select.value="${sel.value}"`;
              continue;
            }
            const radios = Array.from(
              this.variantSelects.querySelectorAll('input[type="radio"][name]')
            ).filter((r) => {
              const n = (r.name || '').trim();
              const dash = n.lastIndexOf('-');
              const stem = dash > 0 ? n.slice(0, dash) : n;
              return stem.toLowerCase() === name.toLowerCase();
            });
            const checked = radios.find((r) => r.checked);
            out[name] = checked
              ? `radio.value="${checked.value}" (of ${radios.length}, ${radios.filter((r) => r.checked).length} checked)`
              : `(none of ${radios.length} options checked)`;
          }
        }
        const variantInput = document.querySelector('[name="id"]');
        out.activeVariantId = variantInput ? variantInput.value : null;
        out.url = window.location.href.includes('?')
          ? '?' + window.location.href.split('?')[1]
          : '(no query)';
        console.log('[wd]', out);
      }

      _hideDimensionsField() {
        const field = this._findDimensionsField();
        if (!field) {
          dbg(`#${this._instanceId} _hideDimensionsField — field not found`);
          return;
        }
        this.dimensionsField = field;
        if (field.getAttribute('data-hidden-by-window-dimensions') !== 'true') {
          field.setAttribute('data-hidden-by-window-dimensions', 'true');
        }
      }

      _scheduleReattach() {
        if (this._scheduledReattach) return;
        this._scheduledReattach = true;
        requestAnimationFrame(() => {
          this._scheduledReattach = false;
          this._refreshRefs(/* invalidateAll */ false);
        });
      }

      _findVariantSelects() {
        const id = this.dataset.variantSelectsId;
        if (id) {
          const byId = document.getElementById(id);
          if (byId) return byId;
        }
        const container = this.closest('[data-section]') || document;
        return (
          container.querySelector('variant-selects') ||
          document.querySelector('variant-selects')
        );
      }

      _findDimensionsField() {
        if (!this.variantSelects) return null;
        const optName = this.optionName.trim();
        const optNameLc = optName.toLowerCase();

        const sel = this.variantSelects.querySelector(
          `select[name="options[${optName}]" i]`
        );
        if (sel) {
          this._dimensionMode = 'select';
          this._dimensionSelect = sel;
          return sel.closest('.product-form__input') || sel.parentElement;
        }

        const radios = this.variantSelects.querySelectorAll('input[type="radio"][name]');
        for (const r of radios) {
          const n = (r.name || '').trim();
          const dash = n.lastIndexOf('-');
          const stem = dash > 0 ? n.slice(0, dash) : n;
          if (stem.toLowerCase() === optNameLc) {
            this._dimensionMode = 'inputs';
            return (
              r.closest('fieldset.product-form__input') ||
              r.closest('.product-form__input') ||
              r.closest('fieldset')
            );
          }
        }

        const fields = this.variantSelects.querySelectorAll('.product-form__input');
        for (const fs of fields) {
          const lab = fs.querySelector('legend, label');
          if (!lab) continue;
          const txt = lab.textContent.trim().toLowerCase();
          if (txt.startsWith(optNameLc)) {
            this._dimensionMode = this._dimensionMode || 'inputs';
            return fs;
          }
        }
        return null;
      }

      _apply() {
        // Always refresh refs first — section may have been replaced since last run.
        this._refreshRefs(/* invalidateAll */ false);

        const w = parseFloat(this.widthInput && this.widthInput.value);
        const h = parseFloat(this.heightInput && this.heightInput.value);

        dbgGroup(`#${this._instanceId} _apply  w=${w} h=${h}`, () => {
          this._logFormState('BEFORE _apply');
        });

        if (!(w > 0) || !(h > 0)) {
          dbg('incomplete — both width and height are required');
          this._setState('incomplete');
          return;
        }

        const candidate = this._findMatch(w, h);

        if (!candidate) {
          dbg('no covering variant — out of range (quote)');
          this._setState('out-of-range');
          return;
        }

        this._setState('ready');

        // Both options updated silently. Track whether either actually changed.
        let secondaryChanged = false;
        let primaryChanged = false;

        if (this.secondaryOptionName) {
          dbg('-- applying SECONDARY (silent) --');
          try {
            secondaryChanged = this._applySecondary(w, /* dispatch */ false);
          } catch (e) {
            dbg('SECONDARY threw — continuing to primary anyway:', e);
          }
          this._logFormState('after secondary set');
        }

        dbg('-- applying PRIMARY (silent) --');
        primaryChanged = this._selectOptionValue(candidate, /* dispatch */ false);
        this._logFormState('after primary set');

        if (!secondaryChanged && !primaryChanged) {
          dbg('nothing changed — not dispatching change event');
          return;
        }

        // Find a LIVE currently-checked primary radio (or live select) and
        // dispatch the change there. We re-find from the live DOM rather
        // than reusing whatever ref _selectOptionValue touched, because
        // setting .checked silently does not invalidate any other state,
        // but ensures the dispatch target is in the live document tree.
        const liveField = this._findDimensionsField();
        const dispatchTarget = this._dimensionMode === 'select' && this._dimensionSelect
          ? this._dimensionSelect
          : (liveField && liveField.querySelector('input[type="radio"]:checked'));

        if (!dispatchTarget) {
          dbg('NO LIVE dispatch target found — variant will not update. This usually means dimensionsField is stale.');
          return;
        }
        if (!document.contains(dispatchTarget)) {
          dbg('dispatchTarget is detached — refusing to dispatch (would silently fail)');
          return;
        }

        dbg(`dispatching single change event on live ${dispatchTarget.tagName.toLowerCase()} (value="${dispatchTarget.value}")`);
        dispatchTarget.dispatchEvent(new Event('change', { bubbles: true }));
        this._logFormState('after change dispatched');

        setTimeout(() => this._logFormState('250ms after dispatch'), 250);
        setTimeout(() => this._logFormState('1000ms after dispatch'), 1000);
      }

      // Parse the "WxH" -> label map into a list of { W, H, label } once.
      _entries() {
        if (this._entriesCache) return this._entriesCache;
        const out = [];
        for (const key of Object.keys(this.map || {})) {
          const [W, H] = key.split('x').map(Number);
          if (Number.isFinite(W) && Number.isFinite(H)) {
            out.push({ W, H, label: this.map[key] });
          }
        }
        this._entriesCache = out;
        return out;
      }

      // Find the tightest REAL variant that covers the entered window.
      //
      // Sizes are discrete and the grid is irregular (not every width pairs
      // with every height), so we never snap each axis independently and
      // recombine — that produces "WxH" keys that don't exist as variants
      // (the original bug). Instead we pick the smallest real variant whose
      // dimensions cover the opening.
      //
      // Orientation is treated as interchangeable (a frame can be installed
      // either way), so we compare on sorted dimensions: the variant's larger
      // side must cover the opening's larger side, and likewise for the
      // smaller. If nothing covers it, return null -> out-of-range (quote).
      _findMatch(w, h) {
        const a = Math.max(w, h);
        const b = Math.min(w, h);
        let best = null;
        let bestMax = 0;
        let bestArea = Infinity;

        for (const e of this._entries()) {
          const eMax = Math.max(e.W, e.H);
          const eMin = Math.min(e.W, e.H);
          if (eMax >= a && eMin >= b) {
            const eArea = eMax * eMin;
            if (eArea < bestArea || (eArea === bestArea && eMax < bestMax)) {
              best = e;
              bestArea = eArea;
              bestMax = eMax;
            }
          }
        }

        dbg(`_findMatch(${w},${h}) → sorted(${a},${b}) → ${best ? best.label : '(no cover)'}`);
        return best ? best.label : null;
      }

      _extractSizes() {
        if (this._sizesCache) return this._sizesCache;
        const pos1 = new Set();
        const pos2 = new Set();
        for (const key of Object.keys(this.map || {})) {
          const [a, b] = key.split('x').map(Number);
          if (Number.isFinite(a)) pos1.add(a);
          if (Number.isFinite(b)) pos2.add(b);
        }
        this._sizesCache = {
          pos1: [...pos1].sort((a, b) => a - b),
          pos2: [...pos2].sort((a, b) => a - b),
        };
        return this._sizesCache;
      }

      _snap(x, sortedList, mode) {
        if (!sortedList || sortedList.length === 0) return null;
        if (mode === 'down') {
          let pick = null;
          for (const v of sortedList) {
            if (v <= x) pick = v;
            else break;
          }
          return pick;
        }
        if (mode === 'nearest') {
          let pick = sortedList[0];
          let bestDist = Math.abs(pick - x);
          for (const v of sortedList) {
            const d = Math.abs(v - x);
            if (d < bestDist || (d === bestDist && v > pick)) {
              pick = v;
              bestDist = d;
            }
          }
          return pick;
        }
        for (const v of sortedList) {
          if (v >= x) return v;
        }
        return null;
      }

      // Single source of truth for the three UI states:
      //   'incomplete'   — one or both boxes empty. Checkout hidden, no message.
      //   'out-of-range' — both filled but nothing covers it. Checkout hidden,
      //                    quote message shown.
      //   'ready'        — a covering variant is selected. Checkout shown.
      //
      // The data-window-state attribute drives the CSS :has() rules (immune to
      // section re-renders); the .product-form classes are a fallback for
      // browsers without :has().
      _setState(state) {
        this.setAttribute('data-window-state', state);

        if (this.warningEl) {
          const showWarning = state === 'out-of-range';
          this.warningEl.toggleAttribute('hidden', !showWarning);
          this.warningEl.classList.toggle('show', showWarning);
        }

        const form = this._findProductForm();
        if (form) {
          form.classList.toggle('product-form--dimensions-incomplete', state === 'incomplete');
          form.classList.toggle('product-form--dimensions-out-of-range', state === 'out-of-range');
        }
      }

      _findProductForm() {
        if (this._productForm && document.contains(this._productForm)) {
          return this._productForm;
        }
        const root = this._stableRoot || document;
        this._productForm = root.querySelector('.product-form');
        return this._productForm;
      }

      // Returns true if the option's value actually changed.
      _selectOptionValue(label, dispatch = true) {
        if (!this.variantSelects) {
          dbg('_selectOptionValue: no variantSelects');
          return false;
        }
        const target = String(label).trim();

        if (this._dimensionMode === 'select' && this._dimensionSelect) {
          const sel = this._dimensionSelect;
          const opt = Array.from(sel.options).find((o) => o.text.trim() === target);
          if (!opt) {
            dbg(`_selectOptionValue [select]: NO OPTION matches "${target}"`);
            return false;
          }
          if (sel.value === opt.value) {
            dbg(`_selectOptionValue [select]: already on "${target}", no-op`);
            return false;
          }
          dbg(`_selectOptionValue [select]: setting "${target}" (dispatch=${dispatch})`);
          sel.value = opt.value;
          if (dispatch) sel.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }

        const root = this.dimensionsField || this.variantSelects;
        const inputs = root.querySelectorAll('input[type="radio"]');
        const allValues = Array.from(inputs).map((r) => (r.value || '').trim());
        for (const input of inputs) {
          const val = (input.value || '').trim();
          let labelText = '';
          if (input.id) {
            const l = root.querySelector(`label[for="${input.id}"]`);
            if (l) labelText = l.textContent.trim();
          }
          if (val === target || labelText === target) {
            if (input.checked) {
              dbg(`_selectOptionValue [radio]: "${target}" already checked, no-op`);
              return false;
            }
            dbg(`_selectOptionValue [radio]: matched "${target}" (dispatch=${dispatch})`);
            input.checked = true;
            if (dispatch) input.dispatchEvent(new Event('change', { bubbles: true }));
            return true;
          }
        }
        dbg(`_selectOptionValue [radio]: NO RADIO matches "${target}". Available: [${allValues.join(' | ')}]`);
        return false;
      }

      // ---------- Secondary option ----------

      _findSecondaryField() {
        if (this._secondaryField) return this._secondaryField;
        if (!this.variantSelects || !this.secondaryOptionName) return null;

        const optName = this.secondaryOptionName;
        const optNameLc = optName.toLowerCase();

        const sel = this.variantSelects.querySelector(
          `select[name="options[${optName}]" i]`
        );
        if (sel) {
          this._secondaryField = {
            field: sel.closest('.product-form__input') || sel.parentElement,
            mode: 'select',
            selectEl: sel,
          };
          dbg(`_findSecondaryField: <select> match for "${optName}"`);
          return this._secondaryField;
        }

        const radios = this.variantSelects.querySelectorAll('input[type="radio"][name]');
        for (const r of radios) {
          const n = (r.name || '').trim();
          const dash = n.lastIndexOf('-');
          const stem = dash > 0 ? n.slice(0, dash) : n;
          if (stem.toLowerCase() === optNameLc) {
            this._secondaryField = {
              field:
                r.closest('fieldset.product-form__input') ||
                r.closest('.product-form__input') ||
                r.closest('fieldset'),
              mode: 'inputs',
              selectEl: null,
            };
            dbg(`_findSecondaryField: radio group match for "${optName}"`);
            return this._secondaryField;
          }
        }

        const fields = this.variantSelects.querySelectorAll('.product-form__input');
        for (const fs of fields) {
          const lab = fs.querySelector('legend, label');
          if (!lab) continue;
          const txt = lab.textContent.trim().toLowerCase();
          if (txt.startsWith(optNameLc)) {
            this._secondaryField = { field: fs, mode: 'inputs', selectEl: null };
            dbg(`_findSecondaryField: legend fallback match for "${optName}"`);
            return this._secondaryField;
          }
        }
        dbg(`_findSecondaryField: NO MATCH for "${optName}"`);
        return null;
      }

      _getSecondaryCandidates() {
        if (this._secondaryCandidates) return this._secondaryCandidates;
        const found = this._findSecondaryField();
        if (!found) return [];

        const intRe = /\d+/;
        const candidates = [];

        if (found.mode === 'select') {
          for (const opt of found.selectEl.options) {
            const text = (opt.value || opt.text || '').trim();
            const m = text.match(intRe);
            if (!m) continue;
            candidates.push({ value: parseInt(m[0], 10), target: text });
          }
        } else {
          const radios = found.field.querySelectorAll('input[type="radio"]');
          for (const r of radios) {
            const text = (r.value || '').trim();
            const m = text.match(intRe);
            if (!m) continue;
            candidates.push({ value: parseInt(m[0], 10), target: text });
          }
        }

        this._secondaryCandidates = candidates;
        dbg('secondary candidates parsed:', candidates);
        return candidates;
      }

      // Returns true if the secondary's value actually changed.
      _applySecondary(rawWidth, dispatch = true) {
        const candidates = this._getSecondaryCandidates();
        if (candidates.length === 0) {
          dbg('_applySecondary: no candidates');
          return false;
        }

        const sortedValues = [...new Set(candidates.map((c) => c.value))].sort((a, b) => a - b);
        const target = this._snap(rawWidth, sortedValues, this.secondarySnapMode);
        dbg(`_applySecondary: width=${rawWidth} → snap[${this.secondarySnapMode}](${rawWidth}, ${JSON.stringify(sortedValues)}) → ${target}`);
        if (target === null) return false;

        const pick = candidates.find((c) => c.value === target);
        if (!pick) return false;

        return this._selectInSecondaryField(pick.target, dispatch);
      }

      // Returns true if the field's value actually changed.
      _selectInSecondaryField(targetText, dispatch = true) {
        const found = this._findSecondaryField();
        if (!found) return false;
        const target = String(targetText).trim();

        if (found.mode === 'select' && found.selectEl) {
          const sel = found.selectEl;
          const opt = Array.from(sel.options).find(
            (o) => (o.value || '').trim() === target || o.text.trim() === target
          );
          if (!opt) {
            dbg(`_selectInSecondaryField [select]: NO OPTION matches "${target}"`);
            return false;
          }
          if (sel.value === opt.value) {
            dbg(`_selectInSecondaryField [select]: already on "${target}", no-op`);
            return false;
          }
          dbg(`_selectInSecondaryField [select]: setting "${target}" (dispatch=${dispatch})`);
          sel.value = opt.value;
          if (dispatch) sel.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }

        const inputs = found.field.querySelectorAll('input[type="radio"]');
        const allValues = Array.from(inputs).map((r) => (r.value || '').trim());
        for (const input of inputs) {
          const val = (input.value || '').trim();
          if (val === target) {
            if (input.checked) {
              dbg(`_selectInSecondaryField [radio]: "${target}" already checked, no-op`);
              return false;
            }
            dbg(`_selectInSecondaryField [radio]: matched "${target}" (dispatch=${dispatch})`);
            input.checked = true;
            if (dispatch) input.dispatchEvent(new Event('change', { bubbles: true }));
            return true;
          }
        }
        dbg(`_selectInSecondaryField [radio]: NO RADIO matches "${target}". Available: [${allValues.join(' | ')}]`);
        return false;
      }
    }
  );
})();